/**
 * StartProjectNudges — yellow Command Center card listing projects
 * that need the owner to press Start.
 *
 * Categories (most urgent first):
 *   - drift_detected        (contractor signaled on-track, no Start pressed)
 *   - overdue               (planned start passed, no Start)
 *   - breaks_ground_today
 *   - breaks_ground_tomorrow
 *
 * Read-only: data comes from GET /api/users/{user_id}/start-reminders.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Rocket, AlertTriangle, Calendar, Hammer } from 'lucide-react';
import { api } from '@/lib/api';

const CATEGORY_CONFIG = {
  drift_detected: {
    icon: AlertTriangle,
    iconBg: '#dc2626',
    label: 'Drift detected',
    bodyTemplate: (n) =>
      `A contractor confirmed they're on track, but Start hasn't been pressed. Phase 1 may already be live — confirm or update the date.`,
    pillBg: 'bg-red-100',
    pillText: 'text-red-800',
    pillBorder: 'border-red-200',
  },
  overdue: {
    icon: AlertTriangle,
    iconBg: '#f59e0b',
    label: (n) => `Overdue · ${n.days_offset}d past planned start`,
    bodyTemplate: (n) =>
      `Planned start was ${n.days_offset} day${n.days_offset === 1 ? '' : 's'} ago. Press Start (or push the date) so contractor reminders and phase handoffs resume.`,
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-800',
    pillBorder: 'border-amber-200',
  },
  breaks_ground_today: {
    icon: Hammer,
    iconBg: '#f59e0b',
    label: 'Breaks ground TODAY',
    bodyTemplate: (n) =>
      `Boots are expected on site today. Press Start to fire the kickoff cascade to your ${n.contractor_count} contractor${n.contractor_count === 1 ? '' : 's'}.`,
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-800',
    pillBorder: 'border-amber-200',
  },
  breaks_ground_tomorrow: {
    icon: Calendar,
    iconBg: '#2563eb',
    label: 'Breaks ground TOMORROW',
    bodyTemplate: (n) =>
      `Press Start in the morning so your ${n.contractor_count} contractor${n.contractor_count === 1 ? '' : 's'} get their kickoff emails on time.`,
    pillBg: 'bg-blue-100',
    pillText: 'text-blue-800',
    pillBorder: 'border-blue-200',
  },
};

export default function StartProjectNudges({ userId }) {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    api
      .get(`/users/${userId}/start-reminders`)
      .then((r) => alive && setNudges(r.data?.nudges || []))
      .catch(() => alive && setNudges([]))
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [userId]);

  if (!loaded || nudges.length === 0) return null;

  return (
    <Card
      className="mb-4 overflow-hidden border-0 shadow-md"
      data-testid="start-project-nudges"
      style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' }}
    >
      <div className="px-4 py-3 flex items-center gap-2 border-b border-amber-300/50">
        <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
          <Bell className="w-3.5 h-3.5 text-white" />
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-amber-900">
          Press Start · {nudges.length} project{nudges.length === 1 ? '' : 's'} need{nudges.length === 1 ? 's' : ''} attention
        </p>
      </div>
      <div className="divide-y divide-amber-300/40">
        {nudges.slice(0, 5).map((n) => {
          const cfg = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.breaks_ground_tomorrow;
          const Icon = cfg.icon;
          const label = typeof cfg.label === 'function' ? cfg.label(n) : cfg.label;
          const body = cfg.bodyTemplate(n);
          return (
            <div
              key={n.project_id}
              className="px-4 py-3 flex items-start gap-3"
              data-testid={`nudge-${n.project_id}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: cfg.iconBg }}
              >
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-bold text-amber-950 truncate">{n.project_name}</p>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${cfg.pillBg} ${cfg.pillText} ${cfg.pillBorder}`}
                  >
                    {label}
                  </span>
                </div>
                <p className="text-xs text-amber-900 leading-snug">{body}</p>
                {n.address && (
                  <p className="text-[10px] text-amber-700 mt-0.5">{n.address}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white hover:bg-amber-50 text-amber-800 border-amber-300 text-xs h-8"
                  onClick={() => navigate(`/project/${n.project_id}?action=push-start`)}
                  data-testid={`nudge-push-${n.project_id}`}
                  title="Push the planned start date — contractor reminders re-arm for the new date"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  Push date
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                  onClick={() => navigate(`/project/${n.project_id}`)}
                  data-testid={`nudge-open-${n.project_id}`}
                >
                  <Rocket className="w-3 h-3 mr-1" />
                  Open project
                </Button>
              </div>
            </div>
          );
        })}
        {nudges.length > 5 && (
          <p className="px-4 py-2 text-[11px] text-amber-700 text-center">
            + {nudges.length - 5} more projects need Start
          </p>
        )}
      </div>
    </Card>
  );
}
